import React, { useState, useEffect } from 'react';
import { useGetRiskProfileQuery } from '../../redux/slices/riskprofile/riskprofileApi';
import { Header, ExportCSVButton, Pagination } from '../../components';
import RiskProfileQuestions from '../../components/riskprofilequestions';
import SearchBox from '../../components/SearchBox';
import { useStateContext } from '../../contexts/ContextProvider';

const RiskProfilePage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const { currentMode } = useStateContext();

  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

 
  const { data, isLoading, error } = useGetRiskProfileQuery({
    page: currentPage,
    search: debouncedSearchTerm,
  });

  const riskProfiles = data?.data || [];
  const totalRecords = data?.total || 0;
  const backendLimit = data?.limit || 10;
  const totalPages = Math.ceil(totalRecords / backendLimit);

  
  const sortRecords = (records, field, direction) =>
    [...records].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      
      if (field === 'created_at') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      
      if (field === 'user_email') {
        aValue = a.user?.email || '';
        bValue = b.user?.email || '';
      }

      if (field === 'user_name') {
        aValue = `${a.user?.first_name || ''} ${a.user?.last_name || ''}`
          .trim()
          .toLowerCase();
        bValue = `${b.user?.first_name || ''} ${b.user?.last_name || ''}`
          .trim()
          .toLowerCase();
      }

      if (field === 'risk_level_name') {
        aValue = a.risk_level?.risk_level || '';
        bValue = b.risk_level?.risk_level || '';
      }

      
      if (
        typeof aValue === 'string' &&
        !['user_name', 'user_email', 'risk_level_name'].includes(field)
      ) {
        aValue = aValue.toLowerCase();
      }
      if (
        typeof bValue === 'string' &&
        !['user_name', 'user_email', 'risk_level_name'].includes(field)
      ) {
        bValue = bValue.toLowerCase();
      }

      
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      if (direction === 'asc') {
        if (aValue > bValue) return 1;
        if (aValue < bValue) return -1;
        return 0;
      }
      if (aValue < bValue) return 1;
      if (aValue > bValue) return -1;
      return 0;
    });

  
  const sortedRecords = sortRecords(riskProfiles, sortField, sortDirection);

 
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); 
  };

  // Get sort indicator
  const getSortIndicator = (field) => {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 ml-1 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg
        className="w-4 h-4 ml-1 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        className="w-4 h-4 ml-1 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.trim());
    setCurrentPage(1); 
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [modalAnswers, setModalAnswers] = useState([]);

  const handleView = (item) => {
    let answers = [];
    try {
      answers = JSON.parse(item.answers);
    } catch (e) {
      answers = [];
    }
    setModalAnswers(answers);
    setModalOpen(true);
  };

  if (isLoading) {
    return (
      <div
        className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${
          currentMode === 'Dark' ? 'bg-secondary-dark-bg' : 'bg-white'
        }`}
      >
        <Header category="Page" title="Risk Profiles" />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${
          currentMode === 'Dark' ? 'bg-secondary-dark-bg' : 'bg-white'
        }`}
      >
        <Header category="Page" title="Risk Profiles" />
        <div className="flex items-center justify-center py-16">
          <div
            className={`text-center rounded-lg p-8 max-w-md mx-auto border ${
              currentMode === 'Dark'
                ? 'bg-gray-800 border-gray-600'
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="mb-4">
              <svg
                className="w-16 h-16 mx-auto text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3
              className={`text-lg font-semibold mb-2 ${
                currentMode === 'Dark' ? 'text-white' : 'text-gray-700'
              }`}
            >
              Unable to Load Risk Profiles
            </h3>
            <p
              className={`text-sm mb-4 ${
                currentMode === 'Dark' ? 'text-gray-300' : 'text-gray-500'
              }`}
            >
              We encountered an issue while loading the risk profile data. Please
              try refreshing the page or contact support if the problem persists.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  
  const renderRiskLevelBadge = (riskLevel) => {
    if (!riskLevel) {
      return '';
    }
    let className =
      'bg-gray-200 text-gray-700 px-3 py-1 rounded-full font-semibold';
    if (riskLevel === 'Aggressive') {
      className =
        'bg-red-100 text-red-800 px-3 py-1 rounded-full font-semibold';
    } else if (riskLevel === 'Growth') {
      className =
        'bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold';
    }
    return <span className={className}>{riskLevel}</span>;
  };

  const getRowBackgroundClass = (index) => {
    if (currentMode === 'Dark') {
      return index % 2 === 0
        ? 'bg-secondary-dark-bg'
        : 'bg-secondary-dark-bg-light';
    }
    return index % 2 === 0 ? 'bg-white' : 'bg-gray-50';
  };

  return (
    <div
      className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${
        currentMode === 'Dark' ? 'bg-secondary-dark-bg' : 'bg-white shadow-lg'
      }`}
    >
      <Header category="Page" title="Risk Profiles" />

      {/* Search Box and Export Button */}
      <div className="mb-4 flex items-center justify-between">
        <SearchBox
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by email..."
        />
        <ExportCSVButton
          data={sortedRecords}
          filename="risk-profiles.xlsx"
          buttonText="Export to Excel"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table
          className={`min-w-max border ${
            currentMode === 'Dark'
              ? 'bg-secondary-dark-bg border-gray-600'
              : 'bg-white border-gray-300'
          }`}
        >
          <thead
            className={
              currentMode === 'Dark'
                ? 'bg-secondary-dark-bg-light'
                : 'bg-gray-50'
            }
          >
            <tr>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === 'Dark'
                    ? 'text-white border-gray-600 hover:bg-gray-700'
                    : 'text-gray-500 border-gray-300'
                }`}
                onClick={() => handleSort('user_id')}
              >
                <div className="flex items-center">
                  User ID
                  {getSortIndicator('user_id')}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === 'Dark'
                    ? 'text-white border-gray-600 hover:bg-gray-700'
                    : 'text-gray-500 border-gray-300'
                }`}
                onClick={() => handleSort('id')}
              >
                <div className="flex items-center">
                  ID
                  {getSortIndicator('id')}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === 'Dark'
                    ? 'text-white border-gray-600 hover:bg-gray-700'
                    : 'text-gray-500 border-gray-300'
                }`}
                onClick={() => handleSort('user_email')}
              >
                <div className="flex items-center">
                  Email
                  {getSortIndicator('user_email')}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === 'Dark'
                    ? 'text-white border-gray-600 hover:bg-gray-700'
                    : 'text-gray-500 border-gray-300'
                }`}
                onClick={() => handleSort('user_name')}
              >
                <div className="flex items-center">
                  Name
                  {getSortIndicator('user_name')}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === 'Dark'
                    ? 'text-white border-gray-600 hover:bg-gray-700'
                    : 'text-gray-500 border-gray-300'
                }`}
                onClick={() => handleSort('total_score')}
              >
                <div className="flex items-center">
                  Total Score
                  {getSortIndicator('total_score')}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === 'Dark'
                    ? 'text-white border-gray-600 hover:bg-gray-700'
                    : 'text-gray-500 border-gray-300'
                }`}
                onClick={() => handleSort('risk_level_name')}
              >
                <div className="flex items-center">
                  Risk Level
                  {getSortIndicator('risk_level_name')}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === 'Dark'
                    ? 'text-white border-gray-600 hover:bg-gray-700'
                    : 'text-gray-500 border-gray-300'
                }`}
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center">
                  Created At
                  {getSortIndicator('created_at')}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b ${
                  currentMode === 'Dark'
                    ? 'text-white border-gray-600'
                    : 'text-gray-500 border-gray-300'
                }`}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody
            className={`divide-y ${
              currentMode === 'Dark'
                ? 'bg-secondary-dark-bg divide-gray-600'
                : 'bg-white divide-gray-200'
            }`}
          >
            {sortedRecords.map((item, index) => (
              <tr key={item.id} className={getRowBackgroundClass(index)}>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                    currentMode === 'Dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {item.user_id}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                    currentMode === 'Dark' ? 'text-white' : 'text-gray-500'
                  }`}
                >
                  {item.id}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === 'Dark' ? 'text-white' : 'text-gray-500'
                  }`}
                >
                  {item.user?.email || ''}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === 'Dark' ? 'text-white' : 'text-gray-500'
                  }`}
                >
                  {`${item.user?.first_name || ''} ${
                    item.user?.last_name || ''
                  }`.trim()}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === 'Dark' ? 'text-white' : 'text-gray-500'
                  }`}
                >
                  {item.total_score}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === 'Dark' ? 'text-white' : 'text-gray-500'
                  }`}
                >
                  {renderRiskLevelBadge(item.risk_level?.risk_level)}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === 'Dark' ? 'text-white' : 'text-gray-500'
                  }`}
                >
                  {item.created_at
                    ? new Date(item.created_at).toLocaleString()
                    : ''}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === 'Dark' ? 'text-white' : 'text-gray-500'
                  }`}
                >
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded-full shadow-md font-semibold hover:bg-blue-700 transition-colors duration-200 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50"
                    onClick={() => handleView(item)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalRecords={totalRecords}
        backendLimit={backendLimit}
        recordsCount={sortedRecords.length}
      />

      {/* No Records Message */}
      {sortedRecords.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <div
            className={`text-center rounded-lg p-8 max-w-md mx-auto border ${
              currentMode === 'Dark'
                ? 'bg-gray-800 border-gray-600'
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="mb-4">
              <svg
                className={`w-16 h-16 mx-auto ${
                  currentMode === 'Dark' ? 'text-gray-500' : 'text-gray-400'
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h3
              className={`text-lg font-semibold mb-2 ${
                currentMode === 'Dark' ? 'text-white' : 'text-gray-700'
              }`}
            >
              No Risk Profiles Found
            </h3>
            <p
              className={`text-sm ${
                currentMode === 'Dark' ? 'text-gray-300' : 'text-gray-500'
              }`}
            >
              {searchTerm
                ? 'No risk profiles match your search criteria. Try adjusting your search terms.'
                : 'There are currently no risk profiles available.'}
            </p>
          </div>
        </div>
      )}

      <RiskProfileQuestions
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        answers={modalAnswers}
      />
    </div>
  );
};

export default RiskProfilePage;
